import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useAuth } from 'contexts/AuthContext';

import DomainBreadcrumbs from 'components/DomainBreadcrumbs';
import Circular from 'components/common/CustomCircular';
import AmountField from 'components/form/AmountField';
import CustomSelect, { SelectOption } from 'components/form/CustomSelect';
import FinancialYearMonthSelect from 'components/form/FinancialYearMonthSelect';
import HourField from 'components/form/HourField';
import MemberSelect from 'components/form/MemberSelect';
import PercentField from 'components/form/PercentField';
import ProjectSelect from 'components/form/ProjectSelect';
import TextArea from 'components/form/TextArea';
import UserSelect from 'components/form/UserSelect';
import ContractConfirmDialog, {
  ConfirmDialogResult,
} from 'pages/contract/components/ContractConfirmDialog';
import TemplateGroupView, { TemplateGroup } from 'pages/contract/components/TemplateGroupView';
import {
  CreateContractRequestParams,
  TemplateItemInput,
  createContractRequest,
} from 'stores/ContractRequestStoreAdapter';
import {
  GetContractTemplateResponse,
  getContractTemplate,
} from 'stores/ContractTemplateStoreAdapter';
import { MasterTypeResult, getMasterType } from 'stores/MasterTypeStoreAdapter';
import { MemberListResult, listMember } from 'stores/MemberStoreAdapter';
import { ProjectListResult, listProject } from 'stores/ProjectStoreAdapter';
import { errorDefault, successDefault } from 'utils/Toastify';

const ContractRequestCreate: React.FC = () => {
  const navigate = useNavigate();
  const { userSession, users } = useAuth();
  const [isProgress, setProgress] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // マスタデータ
  const [projects, setProjects] = useState<ProjectListResult[]>([]);
  const [members, setMembers] = useState<MemberListResult[]>([]);
  const [prefectures, setPrefectures] = useState<MasterTypeResult[]>([]);
  const [contractTypes, setContractTypes] = useState<MasterTypeResult[]>([]);
  const [contractUnits, setContractUnits] = useState<MasterTypeResult[]>([]);
  const [templateData, setTemplateData] = useState<GetContractTemplateResponse | null>(null);

  // フォーム値
  const [personInChargeId, setPersonInChargeId] = useState<string | null>(userSession.userId);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [managerId, setManagerId] = useState<string | null>(
    userSession.roles.includes('MANAGER') ? userSession.userId : null,
  );
  const [memberId, setMemberId] = useState<string | null>(null);
  const [prefectureId, setPrefectureId] = useState('');
  const [location, setLocation] = useState('');
  const [contractTypeId, setContractTypeId] = useState('UNSELECTED');
  const [contractStartDate, setContractStartDate] = useState('');
  const [contractEndDate, setContractEndDate] = useState('');
  const [costAmount, setCostAmount] = useState('');
  const [salesAmount, setSalesAmount] = useState('');
  const [operationRate, setOperationRate] = useState('100');
  const [contractUnitId, setContractUnitId] = useState('');
  const [adjustmentUpperLimit, setAdjustmentUpperLimit] = useState('');
  const [adjustmentLowerLimit, setAdjustmentLowerLimit] = useState('');
  const [needsOriginSheet, setNeedsOriginSheet] = useState(false);
  const [originSheetDestination, setOriginSheetDestination] = useState('');
  const [remarks, setRemarks] = useState('');
  const [templateValues, setTemplateValues] = useState<Record<string, string>>({});
  const [techStackIds, setTechStackIds] = useState<string[]>([]);

  // マスタ取得
  useEffect(() => {
    Promise.all([
      listProject({}),
      listMember({}),
      getMasterType('prefecture'),
      getMasterType('contract_type'),
      getMasterType('contract_unit'),
      getContractTemplate({ visibleOnly: true }),
    ])
      .then(([proj, mem, pref, ctype, cunit, tmpl]) => {
        setProjects(proj);
        setMembers(mem);
        setPrefectures(pref);
        setContractTypes(ctype);
        setContractUnits(cunit);
        setTemplateData(tmpl);
        // セレクト・ラジオのデフォルト: 1個目を選択
        if (pref.length > 0) setPrefectureId(pref[0].typeCode);
        if (cunit.length > 0) setContractUnitId(cunit[0].typeCode);
      })
      .catch(() => errorDefault('マスタデータの取得に失敗しました'));
  }, []);

  // ユーザーリスト（ロール別）
  const managers = useMemo(() => users.filter((u) => u.roles.includes('MANAGER')), [users]);
  const backOfficeUsers = useMemo(
    () => users.filter((u) => u.roles.includes('BACK_OFFICE')),
    [users],
  );

  // 選択中のプロジェクト・メンバー（自動入力用）
  const selectedProject = useMemo(
    () => projects.find((p) => p.projectId === projectId) || null,
    [projects, projectId],
  );
  const selectedMember = useMemo(
    () => members.find((m) => m.memberId === memberId) || null,
    [members, memberId],
  );

  // セレクトオプション
  const prefectureOptions: SelectOption[] = useMemo(
    () => prefectures.map((p) => ({ value: p.typeCode, label: p.typeLabel })),
    [prefectures],
  );

  // テンプレートグループ変換
  const templateGroups: TemplateGroup[] = useMemo(() => {
    if (!templateData) return [];
    return templateData.results
      .filter((g) => g.sortOrder >= 0 || g.groupId === 'FIXED_MEMBER_SKILL')
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((g) => ({
        groupId: g.groupId,
        name: g.name,
        items: g.items
          .filter((i) => !i.isDeleted)
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((i) => ({
            itemKey: i.itemKey,
            label: i.label,
            formType: i.formType,
            selectItems: i.selectItems
              .filter((s) => !s.isDeleted)
              .map((s) => ({ code: s.code, name: s.name })),
          })),
      }));
  }, [templateData]);

  const handleTemplateValueChange = (itemKey: string, value: string) => {
    setTemplateValues((prev) => ({ ...prev, [itemKey]: value }));
  };

  // フォームデータ構築
  const buildParams = (requestStatus: 'ADJUSTING' | 'SUBMITTED'): CreateContractRequestParams => {
    const templateItems: TemplateItemInput[] = Object.entries(templateValues)
      .filter(([, v]) => v)
      .map(([key, value]) => {
        let values: string[];
        try {
          const parsed = JSON.parse(value);
          values = Array.isArray(parsed) ? parsed : [value];
        } catch {
          values = [value];
        }
        return { itemKey: key, values };
      });

    // 技術スタックIDをテンプレート項目に追加
    if (techStackIds.length > 0) {
      const skillGroup = templateGroups.find((g) => g.groupId === 'FIXED_MEMBER_SKILL');
      if (skillGroup && skillGroup.items.length > 0) {
        skillGroup.items.forEach((item) => {
          templateItems.push({ itemKey: item.itemKey, values: techStackIds });
        });
      }
    }

    return {
      requestStatus,
      personInChargeId: personInChargeId || '',
      projectId: projectId || '',
      managerId: managerId || '',
      memberId: memberId || '',
      prefectureId: prefectureId || undefined,
      location: location || undefined,
      contractTypeId: contractTypeId || undefined,
      contractStartDate: contractStartDate || undefined,
      contractEndDate: contractEndDate || undefined,
      costAmount: costAmount ? Number(costAmount) : undefined,
      salesAmount: salesAmount ? Number(salesAmount) : undefined,
      operationRate: operationRate ? Number(operationRate) : undefined,
      contractUnitId: contractUnitId || undefined,
      adjustmentUpperLimit: adjustmentUpperLimit ? Number(adjustmentUpperLimit) : undefined,
      adjustmentLowerLimit: adjustmentLowerLimit ? Number(adjustmentLowerLimit) : undefined,
      needsOriginSheet: needsOriginSheet || undefined,
      originSheetDestination: originSheetDestination || undefined,
      remarks: remarks || undefined,
      templateItems: templateItems.length > 0 ? templateItems : undefined,
    };
  };

  // 一時保存
  const handleSave = () => {
    setProgress(true);
    const params = buildParams('ADJUSTING');
    createContractRequest(params)
      .then(() => {
        successDefault('一時保存しました');
        navigate('/contract/request', { replace: true });
      })
      .catch((e: Error) => errorDefault('保存に失敗しました', e.message))
      .finally(() => setProgress(false));
  };

  // 申請（ダイアログ表示）
  const handleSubmitClick = () => {
    setConfirmOpen(true);
  };

  // 申請確認
  const handleConfirm = (data: ConfirmDialogResult) => {
    setConfirmOpen(false);
    setProgress(true);
    const params: CreateContractRequestParams = {
      ...buildParams('SUBMITTED'),
      applicantUserIds: data.applicantUserIds,
      destinationUserIds: data.destinationUserIds,
    };
    createContractRequest(params)
      .then(() => {
        successDefault('申請しました');
        navigate('/contract/request', { replace: true });
      })
      .catch((e: Error) => errorDefault('申請に失敗しました', e.message))
      .finally(() => setProgress(false));
  };

  // 必須項目バリデーション
  // 一時保存: 基本情報のみ必須
  const isSaveValid = Boolean(personInChargeId && projectId && managerId && memberId);
  // 申請: 基本情報 + 契約情報が必須
  const isSubmitValid = Boolean(
    isSaveValid &&
    contractStartDate &&
    contractEndDate &&
    prefectureId &&
    contractTypeId &&
    costAmount &&
    salesAmount &&
    contractUnitId,
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <DomainBreadcrumbs items={[{ label: '契約管理', to: '/contract' }, { label: '新規申請' }]} />
      {/* ヘッダー */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          新規契約申請
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!isSaveValid}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            一時保存
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSubmitClick}
            disabled={!isSubmitValid}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            申請
          </Button>
        </Stack>
      </Stack>

      {/* フォーム本体 */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Stack spacing={1}>
          {/* 基本情報 */}
          <CollapsibleSection title="基本情報" defaultExpanded>
            <Grid container spacing={2}>
              <Grid size={6}>
                <UserSelect
                  mode="single"
                  users={users}
                  selectedUserId={personInChargeId}
                  onChange={setPersonInChargeId}
                  label="担当者"
                  required
                />
              </Grid>
              <Grid size={6}>
                <UserSelect
                  mode="single"
                  users={managers}
                  selectedUserId={managerId}
                  onChange={setManagerId}
                  label="課長"
                  required
                />
              </Grid>
              <Grid size={6}>
                <ProjectSelect
                  mode="single"
                  projects={projects}
                  selectedProjectId={projectId}
                  onChange={setProjectId}
                  label="プロジェクト"
                  required
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="取引先"
                  value={selectedProject?.clientName || ''}
                  disabled
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </CollapsibleSection>

          {/* メンバー情報 */}
          <CollapsibleSection title="メンバー情報" defaultExpanded>
            <Grid container spacing={2}>
              <Grid size={6}>
                <MemberSelect
                  mode="single"
                  members={members}
                  selectedMemberId={memberId}
                  onChange={setMemberId}
                  label="メンバー"
                  required
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="パートナー"
                  value={selectedMember?.partnerName || '社員'}
                  disabled
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={6}>
                <CustomSelect
                  mode="single"
                  options={prefectureOptions}
                  value={prefectureId}
                  onChange={setPrefectureId}
                  label="都道府県"
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="ロケーション"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </CollapsibleSection>

          {/* 契約情報 */}
          <CollapsibleSection title="契約情報" defaultExpanded>
            <Grid container spacing={2}>
              <Grid size={6}>
                <FormLabel component="legend" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>
                    契約形態
                  </Typography>
                </FormLabel>
                <RadioGroup
                  row
                  value={contractTypeId}
                  onChange={(e) => setContractTypeId(e.target.value)}
                >
                  {contractTypes
                    .filter((ct) => ct.typeCode !== 'EMPLOYEE')
                    .map((ct) => (
                      <FormControlLabel
                        key={ct.typeCode}
                        value={ct.typeCode}
                        control={<Radio size="small" />}
                        label={<Typography variant="body2">{ct.typeLabel}</Typography>}
                      />
                    ))}
                </RadioGroup>
              </Grid>
              <Grid size={6}>
                <FormLabel component="legend" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>
                    契約単位
                  </Typography>
                </FormLabel>
                <RadioGroup
                  row
                  value={contractUnitId}
                  onChange={(e) => setContractUnitId(e.target.value)}
                >
                  {contractUnits.map((cu) => (
                    <FormControlLabel
                      key={cu.typeCode}
                      value={cu.typeCode}
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">{cu.typeLabel}</Typography>}
                    />
                  ))}
                </RadioGroup>
              </Grid>
              <Grid size={6}>
                <FinancialYearMonthSelect
                  dateType="start"
                  value={contractStartDate}
                  onChange={setContractStartDate}
                  label="契約開始日"
                  required
                />
              </Grid>
              <Grid size={6}>
                <FinancialYearMonthSelect
                  dateType="end"
                  value={contractEndDate}
                  onChange={setContractEndDate}
                  label="契約終了日"
                  required
                />
              </Grid>
              <Grid size={6}>
                <AmountField value={costAmount} onChange={setCostAmount} label="契約金額（原価）" />
              </Grid>
              <Grid size={6}>
                <AmountField
                  value={salesAmount}
                  onChange={setSalesAmount}
                  label="契約金額（売上）"
                />
              </Grid>
              <Grid size={6}>
                <PercentField value={operationRate} onChange={setOperationRate} label="稼働率" />
              </Grid>
              <Grid size={6} />
              <Grid size={6}>
                <HourField
                  value={adjustmentUpperLimit}
                  onChange={setAdjustmentUpperLimit}
                  label="精算幅（上限）"
                />
              </Grid>
              <Grid size={6}>
                <HourField
                  value={adjustmentLowerLimit}
                  onChange={setAdjustmentLowerLimit}
                  label="精算幅（下限）"
                />
              </Grid>
            </Grid>
          </CollapsibleSection>

          {/* 補足情報 */}
          <CollapsibleSection title="補足情報" defaultExpanded>
            <Grid container spacing={2}>
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={needsOriginSheet}
                      onChange={(e) => setNeedsOriginSheet(e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">原籍確認シート: 必要</Typography>}
                />
              </Grid>
              {needsOriginSheet && (
                <Grid size={12}>
                  <TextField
                    size="small"
                    fullWidth
                    label="原籍確認シート提出先"
                    value={originSheetDestination}
                    onChange={(e) => setOriginSheetDestination(e.target.value)}
                    slotProps={{ htmlInput: { maxLength: 300 } }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              )}
            </Grid>
          </CollapsibleSection>

          {/* 特記事項 */}
          <CollapsibleSection title="特記事項" defaultExpanded>
            <TextArea value={remarks} onChange={setRemarks} maxLength={10000} rows={4} fullWidth />
          </CollapsibleSection>

          {/* テンプレート項目（見出しなし - 申請者にはテンプレート項目と区別がつかない） */}
          {templateGroups.length > 0 && (
            <TemplateGroupView
              groups={templateGroups}
              mode="edit"
              values={templateValues}
              onValueChange={handleTemplateValueChange}
              techStackIds={techStackIds}
              onTechStackChange={setTechStackIds}
              defaultExpanded
            />
          )}
        </Stack>
      </Box>

      {/* 申請確認ダイアログ */}
      <ContractConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        type="create"
        managers={managers}
        backOfficeUsers={backOfficeUsers}
        currentUserId={userSession.userId}
        contractStartDate={contractStartDate}
      />

      <Circular open={isProgress} />
    </Box>
  );
};

// --- CollapsibleSection ---
type CollapsibleSectionProps = {
  title: string;
  defaultExpanded?: boolean;
  children: React.ReactNode;
};

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  defaultExpanded = true,
  children,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleToggle = () => setExpanded((prev) => !prev);

  return (
    <Accordion
      elevation={0}
      expanded={expanded}
      sx={{
        '&:before': { display: 'none' },
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <AccordionSummary
        expandIcon={
          <IconButton size="small" onClick={handleToggle} sx={{ cursor: 'pointer' }}>
            <ExpandMoreIcon />
          </IconButton>
        }
        sx={{
          px: 2,
          minHeight: 40,
          flexDirection: 'row-reverse',
          gap: 1,
          cursor: 'default',
          '&.Mui-focusVisible': { bgcolor: 'transparent' },
          '& .MuiAccordionSummary-expandIconWrapper': { transform: 'none' },
          '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': { transform: 'none' },
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography
          variant="body2"
          fontWeight={600}
          onClick={handleToggle}
          sx={{ cursor: 'pointer' }}
        >
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>{children}</AccordionDetails>
    </Accordion>
  );
};

export default ContractRequestCreate;
